import React, { useState, useRef } from 'react';
import Modal, { ModalDirectionOptions } from '../components/Modal';
import { useCPRSettings, CPRSettings } from './CPRSettings';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { heIL } from '@mui/material/locale';
import { TextField, Popover } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  runningCprMode?: boolean;
}

interface SecondsPickerProps {
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
  maxSeconds?: number;
}

interface RatePickerProps {
  value: number;
  onChange: (value: number) => void;
}

// Create RTL theme
const theme = createTheme({ typography: { fontFamily: '"Rubik", sans-serif' },
    direction: 'rtl',
    components: {
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiInputBase-input': {
              textAlign: 'center',
            },
          },
        },
      },
    },
  },
  heIL
);

const BorderedSection = styled('div')({
  position: 'relative',
  border: '2px solid #ccc',
  borderRadius: '8px',
  padding: '16px',
  width: '100%',
  boxSizing: 'border-box',
  '@media (max-width: 600px)': {
    padding: '12px',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-2px',
    right: '20px',
    height: '2px',
    width: '120px',
    backgroundColor: 'white',
  }
});

const SectionCaption = styled('div')({
  position: 'absolute',
  top: '-13px',
  right: '25px',
  padding: '0 5px',
  backgroundColor: 'white',
  fontSize: '16px',
  color: '#666',
});

const formatTime = (seconds: number) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

const ListItem = styled('li')({
  justifyContent: 'center',
  cursor: 'pointer',
  padding: '8px 16px',
  listStyle: 'none',
  textAlign: 'center',
  '&:hover': {
    backgroundColor: '#f5f5f5',
  },
  '&.selected': {
    backgroundColor: '#e3f2fd',
    fontWeight: 'bold',
    '&:hover': {
      backgroundColor: '#e3f2fd',
    },
  }
});

const AlertContainer = styled('div')(({ disabled }: { disabled?: boolean }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '10px',
  opacity: disabled ? 0.5 : 1,
  width: '100%',
  '@media (min-width: 600px)': {
    flexDirection: 'row',
    alignItems: 'center',
  }
}));

const AlertLabelContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  minWidth: '200px'
});

const SecondsPickerContainer = styled('div')({
  display: 'inline-block',
  width: '100%',
  maxWidth: '130px',
  '@media (max-width: 600px)': {
    alignSelf: 'stretch'
  }
});

const RatePickerContainer = styled('div')({
  display: 'inline-block',
  width: '100%',
  maxWidth: '130px',
  '@media (max-width: 600px)': {
    alignSelf: 'stretch'
  }
});

const SettingContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '10px',
  width: '100%',
  marginTop: '15px',
  '@media (min-width: 600px)': {
    flexDirection: 'row',
    alignItems: 'center',
  }
});

const SettingLabelContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  minWidth: '200px'
});

const SecondsPickerComponent: React.FC<SecondsPickerProps> = ({
  value,
  onChange,
  disabled = false,
  maxSeconds = 300
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const selectedRef = useRef<HTMLLIElement>(null);

  const timeOptions = Array.from(
    { length: Math.floor(maxSeconds / 5) + 1 },
    (_, index) => index * 5
  );

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
      setTimeout(() => {
        selectedRef.current?.scrollIntoView({ block: 'center' });
      }, 0);
    }
  };

  return (
    <SecondsPickerContainer>
      <TextField
        size="small"
        value={formatTime(value)}
        onClick={handleOpen}
        disabled={disabled}
        InputProps={{
          readOnly: true,
          style: { 
            width: '100%',
            cursor: disabled ? 'default' : 'pointer'
          }
        }}
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        style={{ zIndex: 1500 }}
      >
        <ul style={{
          maxHeight: '300px',
          overflow: 'auto',
          width: '100px',
          margin: 0,
          padding: 0
        }}>
          {timeOptions.map((seconds) => (
            <ListItem
              key={seconds}
              ref={seconds === value ? selectedRef : null}
              className={seconds === value ? 'selected' : ''}
              onClick={() => {
                onChange(seconds);
                setAnchorEl(null);
              }}
            >
              {formatTime(seconds)}
            </ListItem>
          ))}
        </ul>
      </Popover>
    </SecondsPickerContainer>
  );
};

const RatePickerComponent: React.FC<RatePickerProps> = ({
  value,
  onChange
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const selectedRef = useRef<HTMLLIElement>(null);
  const { t } = useTranslation(['resus']);

  const rateOptions = [90, 100, 110, 120, 130];

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setTimeout(() => {
      selectedRef.current?.scrollIntoView({ block: 'center' });
    }, 0);
  };

  return (
    <RatePickerContainer>
      <TextField
        size="small"
        value={`${value} ${t('modals.settings.bpm')}`}
        onClick={handleOpen}
        InputProps={{
          readOnly: true,
          style: { 
            width: '100%',
            cursor: 'pointer'
          }
        }}
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        style={{ zIndex: 1500 }}
      >
        <ul style={{
          maxHeight: '300px',
          overflow: 'auto',
          width: '100px',
          margin: 0,
          padding: 0
        }}>
          {rateOptions.map((rate) => (
            <ListItem
              key={rate}
              ref={rate === value ? selectedRef : null}
              className={rate === value ? 'selected' : ''}
              onClick={() => {
                onChange(rate);
                setAnchorEl(null);
              }}
            >
              {`${t('modals.settings.bpm')} ${rate}`}
            </ListItem>
          ))}
        </ul>
      </Popover>
    </RatePickerContainer>
  );
};

const ModalContent = styled('div')({
  minHeight: '200px',
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
  direction: 'rtl',
  width: '100%',
  maxWidth: '800px',
  margin: '0 auto',
  boxSizing: 'border-box',
  '@media (max-width: 600px)': {
    padding: '12px',
    gap: '16px'
  }
});

const TimerSettingsContainer = styled('div')({
  marginTop: '20px',
  width: '100%'
});

const RadioGroupContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  marginTop: '10px',
  marginRight: '20px',
  width: '100%'
});

const SettingsModal: React.FC<SettingsModalProps> = ({ isOpen, onClose, runningCprMode = false }) => {
  const { settings, updateSettings } = useCPRSettings();
  const [localSettings, setLocalSettings] = useState<CPRSettings>(settings);
  const { t } = useTranslation(['resus']);

  const handleSave = () => {
    updateSettings(localSettings);
    onClose();
  };

  const handleCancel = () => {
    setLocalSettings(settings);
    onClose();
  };

  return (
    <ThemeProvider theme={theme}>
      <Modal
        isOpen={isOpen}
        setIsOpen={() => onClose()}
        title={t('modals.settings.title')}
        primaryButton={{
          text: t('buttons.save'),
          onClick: handleSave,
        }}
        secondaryButton={{
          text: t('buttons.cancel'),
          onClick: handleCancel,
        }}
        direction={ModalDirectionOptions.RTL}
      >
        <ModalContent>
          <BorderedSection>
            <SectionCaption>{t('modals.settings.cprSettings')}</SectionCaption>

            {/* Alert Settings */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '15px',
              width: '100%'
            }}>
              {/* Massager Alert */}
              <AlertContainer disabled={!localSettings.massagerAlertEnabled}>
                <AlertLabelContainer>
                  <input
                    type="checkbox"
                    checked={localSettings.massagerAlertEnabled}
                    onChange={() => setLocalSettings(prev => ({
                      ...prev,
                      massagerAlertEnabled: !prev.massagerAlertEnabled,
                      timerDisplay: !prev.massagerAlertEnabled ? prev.timerDisplay :
                        (prev.timerDisplay === 'massager' ? 'none' : prev.timerDisplay)
                    }))}
                    style={{ marginLeft: '10px' }}
                  />
                  <span>{t('modals.settings.massagerAlert')}</span>
                </AlertLabelContainer>
                <SecondsPickerComponent
                  value={localSettings.massagerAlertSeconds}
                  onChange={(newValue) => {
                    setLocalSettings(prev => ({
                      ...prev,
                      massagerAlertSeconds: newValue
                    }));
                  }}
                  disabled={!localSettings.massagerAlertEnabled || runningCprMode}
                />
              </AlertContainer>

              {/* Adrenaline Alert */}
              <AlertContainer disabled={!localSettings.adrenalineAlertEnabled}>
                <AlertLabelContainer>
                  <input
                    type="checkbox"
                    checked={localSettings.adrenalineAlertEnabled}
                    onChange={() => setLocalSettings(prev => ({
                      ...prev,
                      adrenalineAlertEnabled: !prev.adrenalineAlertEnabled,
                      timerDisplay: !prev.adrenalineAlertEnabled ? prev.timerDisplay :
                        (prev.timerDisplay === 'adrenaline' ? 'none' : prev.timerDisplay)
                    }))}
                    style={{ marginLeft: '10px' }}
                  />
                  <span>{t('modals.settings.adrenalineAlert')}</span>
                </AlertLabelContainer>
                <SecondsPickerComponent
                  value={localSettings.adrenalineAlertSeconds}
                  onChange={(newValue) => {
                    setLocalSettings(prev => ({
                      ...prev,
                      adrenalineAlertSeconds: newValue
                    }));
                  }}
                  disabled={!localSettings.adrenalineAlertEnabled || runningCprMode}
                />
              </AlertContainer>

              {/* Metronome Rate */}
              <SettingContainer>
                <SettingLabelContainer>
                  <span>{t('modals.settings.metronomeRate')}</span>
                </SettingLabelContainer>
                <RatePickerComponent
                  value={localSettings.metronomeBitPerMinute || 100}
                  onChange={(newValue) => {
                    setLocalSettings(prev => ({
                      ...prev,
                      metronomeBitPerMinute: newValue
                    }));
                  }}
                />
              </SettingContainer>
            </div>

            {/* Timer Display Settings */}
            <TimerSettingsContainer>
              <div>{t('modals.settings.timer')}</div>
              <RadioGroupContainer>
                <label>
                  <input
                    type="radio"
                    name="timerDisplay"
                    value="none"
                    checked={localSettings.timerDisplay === 'none'}
                    onChange={() => setLocalSettings(prev => ({
                      ...prev,
                      timerDisplay: 'none'
                    }))}
                    style={{ marginLeft: '10px' }}
                  />
                  {t('modals.settings.dontShow')}
                </label>

                {localSettings.massagerAlertEnabled && (
                  <label>
                    <input
                      type="radio"
                      name="timerDisplay"
                      value="massager"
                      checked={localSettings.timerDisplay === 'massager'}
                      onChange={() => setLocalSettings(prev => ({
                        ...prev,
                        timerDisplay: 'massager'
                      }))}
                      style={{ marginLeft: '10px' }}
                    />
                    {t('modals.settings.showMassagerTime')}
                  </label>
                )}

                {localSettings.adrenalineAlertEnabled && (
                  <label>
                    <input
                      type="radio"
                      name="timerDisplay"
                      value="adrenaline"
                      checked={localSettings.timerDisplay === 'adrenaline'}
                      onChange={() => setLocalSettings(prev => ({
                        ...prev,
                        timerDisplay: 'adrenaline'
                      }))}
                      style={{ marginLeft: '10px' }}
                    />
                    {t('modals.settings.showAdrenalineTime')}
                  </label>
                )}
              </RadioGroupContainer>
            </TimerSettingsContainer>
          </BorderedSection>
        </ModalContent>
      </Modal>
    </ThemeProvider>
  );
};

export default SettingsModal;